<template>
    <b-modal
        size="xl"
        v-model="modalShow"
        title="スケジュール確認"
        @ok="handleOk"
        ok-title="この医師をマッチングする"
        ok-variant="yokumiru-orange"
        :ok-disabled="inProgress"
        cancel-title="閉じる"
        cancel-variant="yokumiru-gray"
        :cancel-disabled="inProgress"
        @hidden="close">
        <div class="t-admin">
            <div v-if="consultation !== null && doctor !== null">
                <div class="detailWrapper">
                    <div class="title">{{ `${$locale.consultation_host}予約情報` }}</div>
                    <ul class="detailList">
                        <li>
                            <div>{{ `${$locale.consultation_host}日時:` }}</div>
                            <div class="d-flex">
                                <div class="u-palt">
                                    <span>{{ helper.dateForHuman(consultation.start_time, timezone) }}</span><!--
                                 --><span :class="helper.dayForHumanCSS(consultation.start_time ,timezone)">
                                        {{ helper.dayForHuman(consultation.start_time, timezone, { bracket: true } ) }}
                                    </span>
                                </div>
                                <div class="u-palt ml-1">
                                    <span>{{ helper.timeForHuman(consultation.start_time, timezone) }}</span><!--
                                 --><span>〜</span><!--
                                 --><span>{{ helper.timeForHuman(consultation.end_time, timezone) }}</span>
                                </div>
                            </div>
                        </li>
                        <li>
                            <div>{{ `${$locale.consultation_host}科:` }}</div>
                            <div>{{ consultation.medical_course.name }}</div>
                        </li>
                        <li>
                            <div>{{ $locale.question }}</div>
                            <div>
                                <a href="#" @click="onClickInterviewAnswersLink(consultation, $event)">
                                    {{ `${$locale.question}を表示する` }}
                                </a>
                            </div>
                        </li>
                    </ul>
                </div>
                <div class="detailWrapper">
                    <div class="title">医師情報</div>
                    <ul class="detailList detailListUser">
                        <li>
                            <div>名前:</div>
                            <div>
                                <span>{{ doctor.full_name }}</span>
                                <span class="text-muted small">{{ `(${doctor.full_name_kana})` }}</span>
                            </div>
                        </li>
                        <li>
                            <div>{{ `${$locale.consultation_host}科` }}:</div>
                            <div>{{ medicalCoursesFormat(doctor.medical_courses) }}</div>
                        </li>
                        <li>
                            <div>タイムゾーン:</div>
                            <div>{{ `${helper.getOffsetFromUTC(doctor.timezone)} ${doctor.timezone}` }}</div>
                        </li>
                    </ul>
                </div>
                <div
                    v-if="inProgress"
                    class="d-flex justify-content-center align-items-center m-4 p-4">
                    <b-spinner variant="yokumiru-admin"></b-spinner>
                </div>
                <div
                    v-if="canShowGanttChart"
                    class="adminMatchingGanttChartWrapper">
                    <g-gantt-chart
                        :chart-start="chartStart"
                        :chart-end="chartEnd"
                        grid
                        :highlighted-hours="[0]"
                        row-label-width="0%"
                        :row-height="54"
                        width="100%">
                        <g-gantt-row
                            v-for="(schedule, index) in chartSchedules"
                            :key="`schedule_${index}`"
                            :label="schedule.label"
                            :bars="schedule.schedules"
                            highlight-on-hover
                            bar-start="start"
                            bar-end="end">
                        </g-gantt-row>
                    </g-gantt-chart>
                </div>
                <div class="axisInfoWrapper d-flex align-items-center">
                    <div class="axisInfo axisInfoAvailable small text-muted">予約可能</div>
                    <div class="axisInfo axisInfoReserved small text-muted">予約済</div>
                    <div v-if="$store.state.configs.reservation_multiple == 1"
                         class="axisInfo axisInfoReservedAlt small text-muted">
                        予約済（空き枠あり）
                    </div>
                    <div class="axisInfo axisInfoMatching small text-muted">{{ `マッチングする${$locale.consultation_host}予約` }}</div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import YokumiruHelper from '../../../YokumiruHelper'
import constants from '../../../constants'
import { GGanttChart, GGanttRow } from 'vue-ganttastic'
import moment from 'moment-timezone'

export default {
    components: {
        GGanttChart,
        GGanttRow,
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            inProgress: false,
            modalShow: false,
            consultation: null,
            doctor: null,
            doctorSchedules: null,
            chartStart: null,
            chartEnd: null,
            timezone: 'Asia/Tokyo',
            errors: {},
        }
    },

    computed: {
        /**
         * スケジュール取得可能判定
         *
         * @return {boolean}
         */
        canFetchSchedules: function () {
            return this.consultation !== null
                && this.doctor !== null
                && this.chartStart !== null
                && this.chartEnd !== null
                && this.endpointData !== null
        },

        /**
         * スケジュール表の表示可能判定
         *
         * @return {boolean}
         */
        canShowGanttChart: function () {
            return this.canFetchSchedules
                && this.doctorSchedules !== null
        },

        /**
         * スケジュール表用に整形されたデータを返す
         *
         * @returns {array}
         */
        chartSchedules: function () {
            if (this.doctorSchedules === null) return []

            const result = []

            // 医師のガントチャート
            for (let [doctorMenuId, items] of Object.entries(this.doctorSchedules)) {
                const tmpSchedules = { label: null, schedules: [], isMatching: false }

                for (let [date, value] of Object.entries(items)) {
                    if (tmpSchedules.label === null) {
                        tmpSchedules.label = value.doctor.full_name
                    }

                    if (!tmpSchedules.isMatching) {
                        tmpSchedules.isMatching = value.doctor_menu.consultation_minutes === this.consultation.medical_course.matching_minutes
                            && value.doctor_menu.consultation_price === this.consultation.medical_course.matching_price
                    }

                    for (const schedule of value.schedules) {
                        const isFullReserved = schedule.is_full_reserved

                        let bgColor = '#F0C72F' // 予約可能

                        // 予約の空き状況に応じて色変更
                        // @see App\Http\Controllers\Admin\Api\DoctorSchedulesController::makeIntervalArray()
                        if (schedule.reserved_doctor_menu_ids.length > 0) {
                            if (isFullReserved) bgColor = '#EE7321'
                            else bgColor = '#bbbbbb' // 予約済（空き枠あり）
                        }
                        if (schedule.start_time === moment(this.consultation.start_time).format('HH:mm')) {
                            bgColor = '#3DB073' // マッチングする相談予約
                        }

                        // @see https://github.com/sewii/Yokumiru/issues/794
                        const endDate = schedule.is_split ? moment(`${date} 00:00:00`).add(1, 'days').format('YYYY-MM-DD') : date

                        tmpSchedules.schedules.push({
                            start: date + ' ' + schedule.start_time,
                            end: endDate + ' ' + schedule.end_time,
                            ganttBarConfig: {
                                color: '#212529',
                                backgroundColor: bgColor,
                                borderRadius: 5,
                            },
                        })
                    }
                }

                // マッチング予約の時間帯にスケジュール枠が無い医師の場合
                if (!this.doctor.has_schedule) {
                    const start = moment(this.consultation.start_time)
                    const end = moment(this.consultation.end_time)
                    tmpSchedules.schedules.push({
                        start: start.format('YYYY-MM-DD HH:mm'),
                        end: end.format('YYYY-MM-DD HH:mm'),
                        ganttBarConfig: {
                            color: '#212529',
                            backgroundColor: '#3DB073',
                            borderRadius: 5,
                        },
                    })
                }

                result.push(tmpSchedules)
            }

            return result
        },

        /**
         * スケジュール取得APIのエンドポイントをクエリパラメータ付きで返却
         *
         * @return {null|object}
         */
        endpointData: function () {
            if (this.consultation === null || this.doctor === null) {
                return null
            }

            const endpoint = constants.admin.doctor_schedules.endpoint.base
            const start = moment(`${this.chartStart} 00:00:00`)
            const end = moment(`${this.chartEnd} 23:59:59`)

            // 指定されたタイムゾーンとJST間の時差を考慮して日付範囲を±1広げる
            const extendedStart = start.clone().subtract(1, 'days')
            const extendedEnd = end

            // スケジュール表の日付範囲を保持
            const rStart = start.clone()
            const rEnd = end.clone()
            const rangeDates = []
            while (rStart.diff(rEnd) <= 0) {
                rangeDates.push(rStart.date())
                rStart.add(1, 'days')
            }

            let query = `?medical_course_id=${this.consultation.medical_course_id}`
            query += `&start_date=${extendedStart.format('YYYY-MM-DD')}`
            query += `&end_date=${extendedEnd.format('YYYY-MM-DD')}`
            query += '&display=doctor'
            query += '&is_matching=0'
            query += `&doctor_id=${this.doctor.id}`
            query += `&consultation_minutes=${this.consultation.medical_course.matching_minutes}`

            return {
                endpoint: `${endpoint}${query}`,
                extendedStart: extendedStart,
                extendedEnd: extendedEnd,
                rangeDates: rangeDates,
            }
        },
    },

    watch: {
        /**
         * スケジュール表の日付フォーマットを変更
         * （vue-ganttasticがフォーマット変更非対応のため）
         *
         * @param {Array} val
         */
        chartSchedules: function (val) {
            if (val && val.length > 0) {
                clearInterval(this.timer)

                this.timer = setInterval(() => {
                    const date = moment(this.chartStart, 'YYYY-MM-DD')
                    const timeAxis = window.document.getElementById('g-timeaxis')

                    if (timeAxis) {
                        const timeAxisDays = window.document.querySelectorAll('#g-timeaxis > div.g-timeaxis-days > div.g-timeaxis-day > div:first-child')
                        timeAxisDays.forEach((timeAxisDay) => {
                            timeAxisDay.innerText = date.format('YYYY年M月D日 (ddd)')
                            date.add(1, 'days')
                        })
                        clearInterval(this.timer)

                        // // vue-ganttastic ライブラリのバグ対応
                        // // 何故か最初の日付だけタイムライン表示されないためjsでDOM追加する
                        // const timeAxisTimes = window.document.querySelectorAll('#g-timeaxis > div.g-timeaxis-days > div.g-timeaxis-day > div:last-child')
                        // timeAxisTimes[0].innerHTML = timeAxisTimes[1].innerHTML
                    }
                }, 100)
            }
        }
    },

    created: function () {
        bus.$on('open-modal-admin-matching-doctor-schedule', this.open)
        bus.$on('admin-doctor-matching-completed', this.close)
    },

    beforeDestroy: function () {
        bus.$off('open-modal-admin-matching-doctor-schedule')
        bus.$off('admin-doctor-matching-completed')
    },

    methods: {
        /**
         * モーダル表示
         * @param {object} consultation
         * @param {object} doctor
         */
        open: function (consultation, doctor) {
            this.consultation = { ...consultation }
            this.doctor = { ...doctor }

            const startTime = moment(consultation.start_time)
            const endTime = moment(consultation.end_time)

            // vue-ganttastic は chartEnd の前日まで表示される仕様

            if (startTime.date() !== endTime.date() && endTime.format('HH:mm') === '00:00') {
                this.chartStart = startTime.format('YYYY-MM-DD')
                this.chartEnd = endTime.format('YYYY-MM-DD')
            }
            else {
                this.chartStart = startTime.format('YYYY-MM-DD')
                this.chartEnd = endTime.add(1, 'days').format('YYYY-MM-DD')
            }

            this.fetchDoctorSchedules()
            this.modalShow = true
        },

        /**
         * モーダルを閉じる
         */
        close: function () {
            this.chartStart = null
            this.chartEnd = null
            this.doctorSchedules = null
            this.modalShow = false
        },

        /**
         * 所属相談科の表示
         * @param {object} courses
         */
        medicalCoursesFormat: function (courses) {
            let result = ''
            courses.forEach((course) => result += `${course.name}、`)
            return result.length > 0 ? result.slice(0, -1) : result
        },

        /**
         * この医師をマッチングするボタン押下時
         * @param {object} e
         */
        handleOk: async function (e) {
            e.preventDefault()
            bus.$emit('open-modal-admin-matching-doctor-confirm', this.consultation, this.doctor)
        },

        /**
         * 相談内容を表示リンク押下時
         * @param {object} consultation
         * @param {object} e
         */
        onClickInterviewAnswersLink: function (consultation, e) {
            e.preventDefault()
            bus.$emit('open-modal-admin-matching-interview-answers', consultation)
        },

        /**
         * 医師スケジュール取得
         */
        fetchDoctorSchedules: async function () {
            if (this.inProgress || !this.canFetchSchedules) return
            this.inProgress = true

            await axios.get(this.endpointData.endpoint)
                .then(({data}) => {
                    this.doctorSchedules = this.convertSchedulesFromJST(
                        data.data,
                        this.endpointData.extendedStart,
                        this.endpointData.extendedEnd,
                        this.endpointData.rangeDates,
                        this.timezone
                    )
                })

            this.inProgress = false
        },

        /**
         * スケジュールの時差変換処理
         *
         * @params {object} data
         * @params {moment} start
         * @params {moment} end
         * @params {array<Number>} rangeDates
         * @params {string} timezone
         *
         * @return {object}
         */
        convertSchedulesFromJST: function (data, start, end, rangeDates, timezone) {
            const convertedData = {}

            for (let [doctorMenuId, items] of Object.entries(data)) {
                const holder = {}
                const loopStart = start.clone()

                while (loopStart.diff(end) <= 0) {
                    const dateFormat = loopStart.format('YYYY-MM-DD')

                    if (items[dateFormat] && items[dateFormat].count > 0) {
                        items[dateFormat].schedules.forEach((schedule) => {
                            const st = this.helper.userTime(`${dateFormat} ${schedule.start_time}`, timezone)
                            const edDateFormat = schedule.is_split ? loopStart.clone().add(1, 'days').format('YYYY-MM-DD') : dateFormat
                            const ed = this.helper.userTime(`${edDateFormat} ${schedule.end_time}`, timezone)

                            // スケジュール表の日付範囲内のみ処理
                            if (rangeDates.includes(st.date())) {
                                schedule.start_time = st.format('HH:mm')
                                schedule.end_time = ed.format('HH:mm')

                                // 開始時間が終了時間より小さい場合のみ処理（サマータイム対策）
                                if (st.diff(ed) < 0 && !this.hasHolder(holder, st, schedule)) {
                                    this.prepareHolder(holder, loopStart, items[dateFormat])
                                    this.prepareHolder(holder, st, items[dateFormat])
                                    this.pushToHolder(holder, st, schedule)
                                }
                            }
                        })
                    }
                    else {
                        // スケジュール表の日付範囲内のみ処理
                        if (rangeDates.includes(loopStart.date())) {
                            this.prepareHolder(holder, loopStart, items[dateFormat])
                        }
                    }
                    loopStart.add(1, 'days')
                }
                convertedData[doctorMenuId] = holder
            }

            return convertedData
        },

        hasHolder: function (holder, date, schedule) {
            if (!holder[date.format('YYYY-MM-DD')]) {
                return false
            }

            const result = holder[date.format('YYYY-MM-DD')].schedules.find((s) => {
                return schedule.start_time === s.start_time && schedule.end_time === s.end_time
            })

            return !!result
        },

        prepareHolder: function (holder, date, item) {
            if (!holder[date.format('YYYY-MM-DD')]) {
                holder[date.format('YYYY-MM-DD')] = {
                    schedules: [],
                    count: 0,
                    day: date.day(),
                    doctor: item['doctor'],
                    doctor_menu: item['doctor_menu'],
                }
            }
        },

        pushToHolder: function (holder, date, schedule) {
            // end が 00:00 フォーマットだと表示が崩れるので 24:00 にする
            if (schedule.end_time === '00:00') {
                schedule.end_time = '24:00'
            }
            holder[date.format('YYYY-MM-DD')].schedules.push(schedule)
            holder[date.format('YYYY-MM-DD')].count += 1
        },
    },
}
</script>

<style scoped lang="scss">
.detailWrapper {
    margin: 10px;
    padding: 15px 20px;
    background: #F3F3F3;
    border-radius: 8px;
    box-sizing: border-box;

    .title {
        font-size: 18px;
        font-weight: bold;
    }

    .detailList {
        padding-left: 0;
        margin: 7px 0 0;

        li {
            display: flex;
        }

        div {
            &:nth-child(1) {
                min-width: 76px;
            }
        }

        a {
            text-decoration: underline;

            &:hover {
                text-decoration: none;
            }
        }
    }

    .detailListUser {
        div {
            &:nth-child(1) {
                min-width: 100px;
            }
        }
    }
}

.adminMatchingGanttChartWrapper {
    margin: 20px 10px 0;
}

.axisInfoWrapper {
    margin-top: 15px;

    .axisInfo {
        margin-right: 15px;
        line-height: 10px;

        &:before {
            position: relative;
            top: 1px;
            content: '';
            display: inline-block;
            width: 10px;
            height: 10px;
            line-height: 10px;
            margin-right: 3px;
        }

        &.axisInfoAvailable {
            &::before {
                background-color: #F0C72F;
            }
        }

        &.axisInfoReserved {
            &::before {
                background-color: #EE7321;
            }
        }

        &.axisInfoReservedAlt {
            &::before {
                background-color: #bbbbbb;
            }
        }

        &.axisInfoMatching {
            &::before {
                background-color: #3DB073;
            }
        }
    }
}
</style>

<style lang="scss">
.adminMatchingGanttChartWrapper {
    .g-gantt-row .g-gantt-row-label {
        padding-left: 0;
    }
}
</style>
